import { useState } from "react";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Tabs, BtnLink } from "../components/index.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon, type IconName } from "../components/Icon.js";

/**
 * 学習分析。閲覧数・完了率・つまずき箇所の集計には専用の計測/集計APIが必要だが、
 * 現状バックエンドに analytics / stats / events 系のエンドポイントは存在しない
 * （apps/api: auth / manuals / translations / uploads / media / stores / members のみ）。
 * よってここでは偽の数値・グラフは一切出さず、各ビューを正直な「準備中」とする。
 * 計測APIが実装され次第、useAsync で実データ化する。
 */

interface PendingView {
  icon: IconName;
  title: string;
  lead: string;
}

const VIEWS: Record<string, PendingView> = {
  overview: {
    icon: "analytics",
    title: "学習分析の概要は準備中です",
    lead: "公開中マニュアルの総閲覧数・完了率・平均所要時間の推移を、この概要にまとめて表示できるようにします。",
  },
  "by-manual": {
    icon: "library",
    title: "マニュアル別の分析は準備中です",
    lead: "マニュアルごとの閲覧数・完了率と、離脱の多い手順をランキングで並べ、改善すべき1本を見つけられるようにします。",
  },
  "by-store": {
    icon: "ops",
    title: "店舗別の分析は準備中です",
    lead: "店舗ごとの学習進捗・完了率を比較し、定着が遅れている現場を早く見つけられるようにします。",
  },
  search: {
    icon: "search",
    title: "検索分析は準備中です",
    lead: "現場でよく検索された言葉と、結果が見つからなかった検索を集計し、不足しているマニュアルを洗い出します。",
  },
  quality: {
    icon: "ai-review",
    title: "品質分析は準備中です",
    lead: "AIレビューの指摘・再視聴の多い手順・離脱率から、手直しが必要なマニュアルを優先度順に提示します。",
  },
};

export function Analytics() {
  const [tab, setTab] = useState("overview");
  const view = VIEWS[tab] ?? VIEWS.overview;

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "分析"]}>
      <PageHead title={<>学習<span className="hl">分析</span></>}>
        閲覧・完了・つまずき箇所から、改善すべきマニュアルを見つける。
      </PageHead>

      <Tabs
        active={tab}
        onChange={setTab}
        ariaLabel="分析ビュー"
        tabs={[
          { id: "overview", label: "概要" },
          { id: "by-manual", label: "マニュアル別" },
          { id: "by-store", label: "店舗別" },
          { id: "search", label: "検索分析" },
          { id: "quality", label: "品質" },
        ]}
      />

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        <EmptyState
          icon={<Icon name={view.icon} size={30} />}
          title={view.title}
          action={
            <BtnLink to="/library" variant="ghost">
              マニュアル一覧を見る
            </BtnLink>
          }
        >
          {view.lead}
          <br />
          学習ログの計測が有効になり次第、ここに実データを表示します。
        </EmptyState>
      </div>
    </ConsoleShell>
  );
}
